<script>
	import { Offcanvas, Table } from "@sveltestrap/sveltestrap";
	import { faPaperclip } from "@fortawesome/free-solid-svg-icons";
	import FontAwesomeIcon from "src/components/FontAwesomeIcon/FontAwesomeIcon.svelte";
	import Lightbox from "../../../components/Lightbox/Lightbox.svelte";
	import { convert } from "src/services/helpers";
	import {
		assignedToLabel,
		jobStatusLabel,
		jobStatusVariantByLabel,
		priorityLabel,
	} from "./jobDisplay";
	import { statusBadgeStyle } from "src/utils/statusBadge";
	import { imageLightbox, imageLightboxCaptions } from "src/store/store";

	export let isOpen = false;
	export let toggle = () => {};
	export let job = null;

	let lightboxOpen = false;
	let lightboxIndex = 0;

	function taskPhotos(task) {
		return (task.media || []).filter((item) => item.type === "Photo");
	}

	function handleOpenTaskImages(task) {
		const photos = taskPhotos(task);
		lightboxIndex = 0;
		imageLightbox.set(photos.map((item) => item.url));
		imageLightboxCaptions.set(photos.map((item) => item.caption || ""));
		lightboxOpen = true;
	}

	$: statusLabel = job ? jobStatusLabel(job.status) : "";

	const cancelReasonLabelByCode = {
		DuplicateJob: "Duplicate job",
		WrongVehicle: "Created against the wrong vehicle",
		NoLongerNeeded: "No longer needed",
		Other: "Other",
		WronglyCreated: "I wrongly created this job",
	};

	function cancelReasonLabel(code) {
		return cancelReasonLabelByCode[code] || code;
	}
</script>

{#if lightboxOpen}
	<Lightbox
		imageShowingIndex={lightboxIndex}
		on:click={() => (lightboxOpen = false)}
	/>
{/if}

<Offcanvas {isOpen} {toggle} placement="end" class="job-detail-panel">
	<div slot="header" class="job-detail-title">
		{#if job}
			<h2>{job.code}</h2>
		{/if}
	</div>

	{#if job}
		<div class="job-detail-row">
			<div class="job-detail-field">
				<span class="job-detail-label">Job status</span>
				<span
					class="status-badge"
					style={statusBadgeStyle(
						jobStatusVariantByLabel[statusLabel]
					)}
				>
					{statusLabel.toUpperCase()}
				</span>
			</div>
			<div class="job-detail-field">
				<span class="job-detail-label">Priority</span>
				<span class="job-detail-value"
					>{priorityLabel(job.priority)}</span
				>
			</div>
		</div>

		<div class="job-detail-row">
			<div class="job-detail-field">
				<span class="job-detail-label">Added by</span>
				<span class="job-detail-value">{job.createdBy || "-"}</span>
			</div>
			<div class="job-detail-field">
				<span class="job-detail-label">Assigned to</span>
				<span class="job-detail-value"
					>{assignedToLabel(job.assignedTo)}</span
				>
			</div>
		</div>

		<div class="job-detail-row">
			<div class="job-detail-field">
				<span class="job-detail-label">Start date and time</span>
				<span class="job-detail-value">{convert(job.start)}</span>
			</div>
			<div class="job-detail-field">
				<span class="job-detail-label">End date and time</span>
				<span class="job-detail-value">{convert(job.due)}</span>
			</div>
		</div>

		<div class="job-detail-tasks">
			<Table responsive hover size="sm">
				<thead>
					<tr>
						<th>Task code</th>
						<th>Task</th>
						<th>Planner's remarks</th>
						<th>Priority</th>
						<th>Status</th>
						<th>Images</th>
					</tr>
				</thead>
				<tbody>
					{#if (job.tasks || []).length === 0}
						<tr>
							<td colspan="6">
								<b class="d-flex justify-content-center">
									No tasks for this job
								</b>
							</td>
						</tr>
					{:else}
						{#each job.tasks as task}
							<tr>
								<td>{task.code}</td>
								<td>{task.name}</td>
								<td>{task.plannerRemarks || "-"}</td>
								<td>{priorityLabel(task.priority)}</td>
								<td>
									<span
										class="status-badge"
										style={statusBadgeStyle(
											jobStatusVariantByLabel[
												jobStatusLabel(task.status)
											]
										)}
									>
										{jobStatusLabel(
											task.status
										).toUpperCase()}
									</span>
								</td>
								<td>
									{#if taskPhotos(task).length}
										<button
											type="button"
											style="background:none;border:none;padding:0;cursor:pointer;color:inherit;vertical-align:middle;"
											title={`View ${
												taskPhotos(task).length
											} image${
												taskPhotos(task).length === 1
													? ""
													: "s"
											}`}
											on:click={() =>
												handleOpenTaskImages(task)}
										>
											<span class="visually-hidden">
												{`View ${
													taskPhotos(task).length
												} images`}
											</span>
											<FontAwesomeIcon
												icon={faPaperclip}
											/>
										</button>
									{:else}
										-
									{/if}
								</td>
							</tr>
						{/each}
					{/if}
				</tbody>
			</Table>
		</div>

		{#if job.cancelReasonCode}
			<div class="job-detail-row">
				<div class="job-detail-field">
					<span class="job-detail-label">Reason for cancelling</span>
					<span class="job-detail-value"
						>{cancelReasonLabel(job.cancelReasonCode)}</span
					>
				</div>
			</div>
		{/if}
	{/if}
</Offcanvas>

<style>
	:global(.job-detail-panel) {
		width: 516px !important;
		padding: 40px 24px;
	}

	:global(.job-detail-panel .offcanvas-body) {
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: 24px;
	}

	:global(.job-detail-panel .offcanvas-header) {
		padding: 0;
		margin-bottom: 32px;
	}

	.job-detail-title h2 {
		font-size: 24px;
		font-weight: bold;
		margin: 0;
		color: var(--fds-on-surface, #00071f);
	}

	.job-detail-row {
		display: flex;
		gap: 16px;
		width: 100%;
	}

	.job-detail-field {
		flex: 1 1 0;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.job-detail-label {
		font-size: 14px;
		color: var(--fds-on-surface-variant, #595959);
	}

	.job-detail-value {
		font-size: 14px;
		color: var(--fds-on-surface, #00071f);
	}

	.status-badge {
		display: inline-block;
		width: fit-content;
		border-radius: 4px;
		padding: 4px 8px;
		font-size: 12px;
		font-weight: bold;
	}

	.job-detail-tasks {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}
</style>
